import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import { ErrorHandlingService } from '../../../services/error-handling.service';
import { SidenavService } from '../../../services/sidenav.service';
import { SiteService } from '../../../services/site.service';
import { UserPermissionService } from '../../../services/user-permission.service';
import { MainNavItem } from '../../../types/navigation';
import { UserProfileComponent } from '../../../auth/user-profile/user-profile.component';

@Component({
  selector: 'app-main-nav',
  templateUrl: './main-nav.component.html',
  styleUrl: './main-nav.component.scss',
  imports: [
    RouterModule,
    MatIconModule,
    UserProfileComponent
  ]
})
export class MainNavComponent implements OnInit {
  private readonly permissionService = inject(UserPermissionService);

  /** The full nav as configured; `mainNav` filters this against the current user. */
  private readonly allNav = signal<MainNavItem[]>([]);

  /**
   * The nav items the current user may see. An item shows when it has no permission/role (open to any
   * signed-in user), when the user holds any one of its permissions, or when the user holds its role.
   * Reads the permission signals, so it fills in on its own once permissions finish loading.
   */
  readonly mainNav = computed(() =>
    this.allNav().filter((item) => this.canShow(item))
  );

  currentUrl!: string;

  constructor(
    private siteService: SiteService,
    private errorService: ErrorHandlingService,
    protected sidenavService: SidenavService,
    private router: Router
  ) { }

  ngOnInit() {
    this.getMainNav();

    this.currentUrl = this.router.url;

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.currentUrl = event.urlAfterRedirects || event.url;
    });
  }

  getMainNav() {
    this.siteService.getMainNav().subscribe({
      next: (res: MainNavItem[]) => this.allNav.set(res),
      error: (err: any) => this.errorService.handleError(err, 'Error Loading Site')
    });
  }

  private canShow(item: MainNavItem): boolean {
    if (item.role) {
      return this.permissionService.hasRole(item.role);
    }

    if (item.permission) {
      const required = typeof item.permission === 'string' ? [item.permission] : item.permission;
      return this.permissionService.hasAny(required);
    }

    return true;
  }

  // Returns true when the provided nav path is a parent of the current URL.
  // Example: path '/vehicles' is parent of '/vehicles/123/details'
  isActive(item: MainNavItem): boolean {
    const path = item.path;
    if (!path) return false;

    // Normalize trailing slashes
    const normalize = (s: string) => s.replace(/\/+$/, '');
    /** Compare path only so query params (e.g. ?view=favorites on /maintenance) do not break active state. */
    const stripQueryAndHash = (s: string) => {
      const q = s.indexOf('?');
      const h = s.indexOf('#');
      let end = s.length;
      if (q >= 0) {
        end = Math.min(end, q);
      }
      if (h >= 0) {
        end = Math.min(end, h);
      }
      return s.slice(0, end);
    };

    const nav = normalize(path);
    const cur = normalize(stripQueryAndHash(this.currentUrl || ''));

    if (!nav) return false; // skip empty root path as parent
    if (cur === nav) return true;

    return cur.startsWith(nav + '/');
  }
}
